import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Input,
  Form,
  notification,
  Button,
  styled,
  Flex,
  Image
} from 'core-ui';
import { LoginFormI } from 'src/models/auth';
import { notifyMessage } from 'src/constants/message';
import { login } from 'src/services/auth';
import { RoutePath } from 'src/enums/routePath';
import {
  ACCESS_TOKEN_KEY,
  IS_REDIRECT_TO_CHANGE_PASSWORD,
  ROLE_COMPANY_SUPER_ADMIN,
  ROLE_SYSTEM_ADMIN,
  ROLE_SYSTEM_SUPER_ADMIN
} from 'src/constants/auth';
import { checkIsSystemSuperAdminOrSystemAdmin } from 'src/utils/common';
import { useEffect, useMemo, useState } from 'react';
import { getProfile } from 'src/services/profile';
import Cookies from 'js-cookie';

const StyledInputPw = styled(Input)`
  padding: 0px 0px !important;
  .ant-input {
    border-top-left-radius: 4px !important;
    border-bottom-left-radius: 4px !important;
    height: 38px !important;
    padding-left: 11px !important;
  }
  .ant-input-suffix {
    padding-right: 11px !important;
  }
`;

export const permissionRouteMap: Record<string, string> = {
  company_registration: RoutePath.COMPANY_REGISTRATION_MANAGEMENT,
  company: RoutePath.COMPANY,
  company_admin: RoutePath.COMPANY_ADMIN,
  company_staff: RoutePath.COMPANY_STAFF,
  scanning_histories: RoutePath.HISTORY_SCAN,
  company_product: RoutePath.PRODUCT,
  reports: RoutePath.REPORT,
  product_sticker_order: RoutePath.STICKER,
  licence_renewal_order: RoutePath.RENEWAL,
  analysis: RoutePath.ANALYTIC
};

const LoginPage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  const state = location.state as {
    passwordChanged?: boolean;
    success?: boolean;
    message?: string;
  };

  const isLogined = useMemo(() => {
    return !!Cookies.get(ACCESS_TOKEN_KEY);
  }, []);

  const handleRedirectPageForSystemAdmin = (permissions: string[]) => {
    const order = Object.keys(permissionRouteMap);
    const sortedPermissions = [...permissions]?.sort(
      (a, b) => order.indexOf(a) - order.indexOf(b)
    );
    const matchedRoute = sortedPermissions?.find(
      (permission) => permissionRouteMap[permission]
    );

    if (matchedRoute) {
      navigate(permissionRouteMap[matchedRoute]);
    } else {
      navigate(RoutePath.NOT_PERMISSION);
    }
  };

  const handleLogin = async (form: LoginFormI) => {
    try {
      setIsLoading(true);
      const res = await login({
        email: form?.email?.trim(),
        password: form?.password?.trim()
      });
      setIsLoading(false);
      const user = res?.data?.data?.user;
      const permissionKeys = user?.permissions?.map(
        (item: any) => item?.permission_key
      );
      const isRedirectToChangePassword =
        user?.role === ROLE_COMPANY_SUPER_ADMIN && !user?.change_password_at;
      if (isRedirectToChangePassword) {
        localStorage.setItem(IS_REDIRECT_TO_CHANGE_PASSWORD, 'true');
        navigate(RoutePath.CHANGE_PASSWORD);
      } else if (user?.role === ROLE_SYSTEM_SUPER_ADMIN) {
        localStorage.setItem(IS_REDIRECT_TO_CHANGE_PASSWORD, 'false');
        navigate(RoutePath.COMPANY_REGISTRATION_MANAGEMENT);
      } else if (user?.role === ROLE_SYSTEM_ADMIN) {
        localStorage.setItem(IS_REDIRECT_TO_CHANGE_PASSWORD, 'false');
        handleRedirectPageForSystemAdmin(permissionKeys);
      } else {
        localStorage.setItem(IS_REDIRECT_TO_CHANGE_PASSWORD, 'false');
        navigate(
          `${RoutePath.COMPANY}${RoutePath.DETAIL}/${user?.company?.id}`
        );
      }
    } catch (err) {
      setIsLoading(false);
      if ((err as any)?.status === 429) {
        notification.error({
          message: (err as any)?.response?.data?.message
        });
      } else {
        notification.error({
          message:
            (err as any)?.response?.data?.errors?.email ||
            notifyMessage.loginError
        });
      }
    } finally {
    }
  };

  const handleGetProfile = async () => {
    if (!isLogined) return;
    try {
      const res = await getProfile({ with: 'company', with_count: '' });
      const isRedirectToChangePassword =
        res?.data?.role === ROLE_COMPANY_SUPER_ADMIN &&
        !res?.data?.change_password_at;
      if (isRedirectToChangePassword) {
        navigate(RoutePath.CHANGE_PASSWORD);
      } else if (checkIsSystemSuperAdminOrSystemAdmin(res?.data?.role)) {
        navigate(RoutePath.COMPANY_REGISTRATION_MANAGEMENT);
      } else {
        navigate(
          `${RoutePath.COMPANY}${RoutePath.DETAIL}/${res?.data?.company_id}`
        );
      }
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    handleGetProfile();
  }, []);
  return (
    <div
      className="h-[100vh] flex bg-cover bg-center"
      style={{ backgroundImage: "url('/login-background.png')" }}
    >
      <div className="justify-center items-center flex-1 flex text-primary">
        <div className="bg-white w-[98vw] sm:w-[558px] flex flex-col rounded-[16px] px-[24px] pt-[40px] pb-[29px]">
          {!(state?.success && state?.message) && (
            <Image
              src="/logo-login.png"
              alt="Tree Crops Development Authority Oil Palm Digital Platform"
              width={79}
              height={79}
              preview={false}
              wrapperClassName="m-auto"
            />
          )}

          <p className="font-medium text-[30px] max-w-[510px] text-center mb-[60px]">
            {state?.success && state?.message
              ? 'TCDA - OilPalm Digital Platform'
              : 'Tree Crops Development Authority Oil Palm Digital Platform'}
          </p>
          {state?.success && state?.message && (
            <Flex
              className={`text-[14px] leading-[22px] p-[10px] mb-[12px] rounded-[3px] mx-[30px] items-center gap-x-[8px] ${state?.passwordChanged ? 'text-[#FFF7E6] bg-[#FAAD14]' : 'text-[#52C41A] bg-[#F6FFED]'}`}
            >
              {!state?.passwordChanged && (
                <Image
                  src="/svg/check-circle.svg"
                  alt="success-icon"
                  width={16}
                  height={16}
                  preview={false}
                />
              )}

              {state.message}
            </Flex>
          )}
          <Form
            layout="vertical"
            form={form}
            name="control-hooks"
            onFinish={handleLogin}
            style={{ padding: '0 30px' }}
          >
            <Input
              label="Email"
              name="email"
              placeholder="Enter your email"
              rules={[
                { required: true },
                { type: 'email', message: 'Email is invalid' }
              ]}
              className="h-[40px]"
            />
            <StyledInputPw
              label="Password "
              inputType="password"
              name="password"
              type="password"
              placeholder="Enter your password"
              rules={[{ required: true }]}
              className="h-[40px]"
            />
            <Form.Item className="mt-[36px] !mb-0">
              <Button
                htmlType="submit"
                type="primary"
                className="w-full h-[40px]"
                disabled={isLoading}
              >
                Login
              </Button>
            </Form.Item>
            {!(state?.success && state?.message) && (
              <Flex className="justify-center mt-[24px]">
                <Link className="text-[#1677ff]" to={RoutePath.FORGOT_PASSWORD}>
                  Forgot your password?
                </Link>
              </Flex>
            )}
          </Form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
